import { useState, useEffect } from 'react';

const HERO_PORTRAITS = [
  { image: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/fia5egs9_5461498A-5D71-4307-AECE-DF17C0930091.jpeg", position: "center 30%", mood: "The work. The world. What comes next.", logo: { left: '8%', bottom: '15%', textAlign: 'left' } },
  { image: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/bgvb9t31_9CD78097-A8E1-4665-AE09-EC378065E18D.jpeg", position: "center 15%", mood: "Every role is a doorway.", logo: { left: '8%', top: '20%', textAlign: 'left' } },
  { image: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/r1kcbxoe_DSC_7296.jpeg", position: "center 25%", mood: "Unscripted. Unbroken.", logo: { right: '8%', bottom: '15%', textAlign: 'right' } },
  { image: "https://customer-assets.emergentagent.com/job_avinash-studio/artifacts/vesfy3yi_DSC_6170.jpeg", position: "center 35%", mood: "What comes next.", logo: { right: '8%', top: '20%', textAlign: 'right' } },
];

const PARTICLES = Array.from({ length: 30 }, () => ({
  left: `${Math.random() * 100}%`,
  delay: `${Math.random() * 10}s`,
  duration: `${8 + Math.random() * 12}s`,
  size: `${1 + Math.random() * 2}px`,
}));

export default function HeroSection({ visible }) {
  const [idx, setIdx] = useState(0);
  const [logoReady, setLogoReady] = useState(false);
  const [dotReady, setDotReady] = useState(false);

  useEffect(() => {
    if (visible) {
      const t1 = setTimeout(() => setLogoReady(true), 300);
      const t2 = setTimeout(() => setDotReady(true), 1800);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [visible]);

  useEffect(() => {
    const interval = setInterval(() => setIdx(i => (i + 1) % HERO_PORTRAITS.length), 7000);
    return () => clearInterval(interval);
  }, []);

  const current = HERO_PORTRAITS[idx];
  const logoPos = current.logo;

  return (
    <section data-testid="hero-section" className="relative w-full h-[100svh] overflow-hidden bg-hero" style={{ opacity: visible ? 1 : 0, transition: 'opacity 1s ease' }}>
      {/* Portrait backgrounds */}
      {HERO_PORTRAITS.map((h, i) => (
        <div key={i} className="absolute inset-0" style={{ opacity: idx === i ? 1 : 0, transition: 'opacity 1.6s var(--ease-out-expo)', zIndex: idx === i ? 1 : 0 }}>
          <img src={h.image} alt="" className="w-full h-full object-cover animate-breathe-zoom" style={{ objectPosition: h.position }} loading={i === 0 ? 'eager' : 'lazy'} />
        </div>
      ))}

      {/* Warm overlay */}
      <div className="absolute inset-0 z-[2]" style={{ background: 'radial-gradient(ellipse at center, rgba(10,10,10,0.25) 0%, rgba(10,10,10,0.7) 100%)' }} />
      <div className="absolute inset-0 z-[2] bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />

      {/* Gold particles */}
      <div className="absolute inset-0 z-[3] pointer-events-none overflow-hidden">
        {PARTICLES.map((p, i) => (
          <div key={i} className="gold-particle" style={{ left: p.left, bottom: '-10px', width: p.size, height: p.size, animationDelay: p.delay, animationDuration: p.duration }} />
        ))}
      </div>

      {/* AT. mark — smart positioning per image */}
      <div
        className="absolute z-[4] pointer-events-none"
        style={{
          ...logoPos,
          transition: 'all 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        }}
      >
        <div style={{ textAlign: logoPos.textAlign }}>
          {/* AT letters — cinematic entrance */}
          <h1 className="font-['Playfair_Display'] italic font-normal text-[16vw] md:text-[11vw] lg:text-[9vw] at-shimmer" style={{
            lineHeight: 0.85,
            paddingLeft: '0.15em',
            paddingRight: '0.05em',
            opacity: logoReady ? 1 : 0,
            transform: logoReady ? 'scale(1)' : 'scale(1.5)',
            filter: logoReady ? 'blur(0)' : 'blur(20px)',
            transition: 'opacity 1.5s cubic-bezier(0.165, 0.84, 0.44, 1), transform 1.5s cubic-bezier(0.165, 0.84, 0.44, 1), filter 1.5s cubic-bezier(0.165, 0.84, 0.44, 1)',
          }}>
            AT
            <span className="at-shimmer-dot" style={{
              display: 'inline-block',
              opacity: dotReady ? 1 : 0,
              transform: dotReady ? 'scale(1)' : 'scale(0)',
              transition: 'opacity 0.3s ease, transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
            }}>.</span>
          </h1>
          <p className="font-['Cormorant_Garamond'] text-sm md:text-base tracking-[0.15em] mt-4" style={{
            color: 'var(--warm-cream)',
            opacity: logoReady ? 0.55 : 0,
            transition: 'opacity 0.8s ease 1s',
          }}>
            {current.mood}
          </p>
        </div>
      </div>

      {/* Dot indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[4] flex gap-2">
        {HERO_PORTRAITS.map((_, i) => (
          <div key={i} className="w-1.5 h-1.5 rounded-full transition-all duration-500" style={{ background: idx === i ? 'var(--gold)' : 'rgba(255,255,255,0.15)', transform: idx === i ? 'scale(1.4)' : 'scale(1)' }} />
        ))}
      </div>

      {/* Shimmer CSS */}
      <style>{`
        .at-shimmer {
          background: linear-gradient(
            90deg,
            var(--gold) 0%,
            var(--gold-light) 25%,
            var(--gold) 50%,
            var(--gold-light) 75%,
            var(--gold) 100%
          );
          background-size: 200% 100%;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmerSweep 4s linear infinite;
        }
        @keyframes shimmerSweep {
          0% { background-position: 200% center; }
          100% { background-position: -200% center; }
        }
      `}</style>
    </section>
  );
}
